/**
 * **Alive buttons** — the arithmetic behind a gradient label that stays legible
 * (docs/console-redesign-plan.md §3 B9; the owner, 2026-09-26: "make sure the
 * buttons we have support animated gradients … I want things to look modern
 * and alive").
 *
 * Three surfaces offer the same three words — the guest site's `button.fill`
 * token (`@yourstore/theme`), the staff app's `buttons` named-set field, the
 * console's own primary — and every one of them paints a two-stop gradient
 * under a label. The guardrail they share is the one a gradient makes easy to
 * break: **the label has to clear WCAG AA (4.5:1) against every stop**, not
 * against the brand colour the gradient started from. A lighter stop under
 * white type, or a darker one under ink, is exactly how a solid button that
 * passed becomes a moving one that fails at one end of its drift.
 *
 * So the stops are *derived*, not configured, and the derivation is here — in
 * `@yourstore/domain`, beside `accentSoftWash`, because the guest bundle and the
 * staff app both import this package and a second copy of a contrast rule is a
 * rule that drifts. Pure and dependency-free: hex in, hex out.
 *
 * ## The rule
 *
 *  1. The gradient runs from the accent to a second stop — the second accent
 *     when the tenant has a distinct one, otherwise the accent moved a fifth of
 *     the way *away from the label* (deeper under a light label, lighter under
 *     a dark one), so the drift never travels toward the text.
 *  2. Each candidate label (the caller's list, most-preferred first) is tried.
 *     If it fails at a stop, both stops step away from it, 6 % at a time, until
 *     it passes. The candidate needing the fewest steps wins; a tie goes to the
 *     earlier one — so a brand whose own accent ink already works keeps it.
 *  3. "Passes" means at rest **and** under the hover/press brightening
 *     (`HOVER_BRIGHTNESS`, which the stylesheets apply as `filter:
 *     brightness()`), so the brighter-on-hover the brief asks for can never be
 *     the state that drops below AA.
 */
/** WCAG 2.x AA for body-size text: the bar every label is held to. */
export const AA_TEXT_CONTRAST = 4.5;
/**
 * The hover/press lift, as the `brightness()` factor every surface's
 * stylesheet uses. Stated here because the contrast check has to measure the
 * brightened state too; a stylesheet that raised it without raising this
 * would be brightening past what was checked.
 */
export const HOVER_BRIGHTNESS = 1.06;
/** The three words, in the order a control offers them. `solid` is today. */
export const BUTTON_FILLS = ['solid', 'gradient', 'animated'];
export function isButtonFill(value) {
    return typeof value === 'string' && BUTTON_FILLS.includes(value);
}
/** `#abc` / `#aabbcc` → 0–255 channels; `null` for anything else. */
export function parseHexColor(value) {
    if (value === undefined)
        return null;
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim())?.[1];
    if (hex === undefined)
        return null;
    const full = hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex;
    return [
        Number.parseInt(full.slice(0, 2), 16),
        Number.parseInt(full.slice(2, 4), 16),
        Number.parseInt(full.slice(4, 6), 16),
    ];
}
function toHex([r, g, b]) {
    const part = (n) => Math.round(Math.min(255, Math.max(0, n)))
        .toString(16)
        .padStart(2, '0');
    return `#${part(r)}${part(g)}${part(b)}`;
}
/** WCAG relative luminance of a hex colour (0 black … 1 white). */
export function relativeLuminance(color) {
    const rgb = parseHexColor(color);
    if (rgb === null)
        throw new Error(`not a hex colour: ${color}`);
    const linear = (channel) => {
        const c = channel / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * linear(rgb[0]) + 0.7152 * linear(rgb[1]) + 0.0722 * linear(rgb[2]);
}
/** WCAG contrast ratio between two hex colours, 1 … 21. */
export function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const [hi, lo] = la > lb ? [la, lb] : [lb, la];
    return (hi + 0.05) / (lo + 0.05);
}
/** `a` moved `t` (0…1) of the way to `b`, in sRGB — what `color-mix(in srgb)` does. */
export function mixHexColors(a, b, t) {
    const x = parseHexColor(a);
    const y = parseHexColor(b);
    if (x === null || y === null)
        throw new Error(`not hex colours: ${a}, ${b}`);
    return toHex([x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t]);
}
/** CSS `filter: brightness(f)` on a hex colour: each channel scaled, clamped. */
export function brightenHexColor(color, factor = HOVER_BRIGHTNESS) {
    const rgb = parseHexColor(color);
    if (rgb === null)
        throw new Error(`not a hex colour: ${color}`);
    return toHex([rgb[0] * factor, rgb[1] * factor, rgb[2] * factor]);
}
/**
 * The lowest contrast `label` reaches against any of `stops`, at rest or
 * brightened. The number a test holds to `AA_TEXT_CONTRAST`.
 */
export function minLabelContrast(label, stops) {
    let min = Infinity;
    const lit = brightenHexColor(label);
    for (const stop of stops) {
        min = Math.min(min, contrastRatio(label, stop), contrastRatio(lit, brightenHexColor(stop)));
    }
    return min;
}
const SECOND_STOP_SHIFT = 0.2;
const STEP = 0.06;
const MAX_STEPS = 40;
/**
 * Derive a legible two-stop gradient from an accent (and an optional second
 * accent) for the first label candidate that can be made to pass with the
 * fewest steps. `null` when the accent is not a hex colour (a tenant who wrote
 * `oklch(…)` keeps the stylesheet's fallback) or, in principle, when no
 * candidate passes — which cannot happen for a light or dark label, because
 * stepping toward black or white ends at 21:1.
 */
export function buttonGradient(input) {
    const accentRgb = parseHexColor(input.accent);
    if (accentRgb === null)
        return null;
    const accent = toHex(accentRgb);
    const secondRgb = parseHexColor(input.second);
    const second = secondRgb === null ? null : toHex(secondRgb);
    let best = null;
    const seen = new Set();
    for (const candidate of input.labels) {
        const rgb = parseHexColor(candidate);
        if (rgb === null)
            continue;
        const label = toHex(rgb);
        if (seen.has(label))
            continue;
        seen.add(label);
        // Away from the label: black under a light one, white under a dark one.
        // 0.179 is where a colour's contrast with white equals its contrast with
        // black, so it is the honest line between "light" and "dark".
        const pole = relativeLuminance(label) > 0.179 ? '#000000' : '#ffffff';
        let from = accent;
        let to = second !== null && second !== accent ? second : mixHexColors(accent, pole, SECOND_STOP_SHIFT);
        let steps = 0;
        while (minLabelContrast(label, [from, to]) < AA_TEXT_CONTRAST && steps < MAX_STEPS) {
            from = mixHexColors(from, pole, STEP);
            to = mixHexColors(to, pole, STEP);
            steps += 1;
        }
        if (minLabelContrast(label, [from, to]) < AA_TEXT_CONTRAST)
            continue;
        if (best === null || steps < best.steps)
            best = { from, to, ink: label, steps };
    }
    return best === null ? null : { from: best.from, to: best.to, ink: best.ink };
}
//# sourceMappingURL=button-fill.js.map